/**
 * The images sent in this page's lifetime, so a question can show the picture it was asked about.
 * Nothing is stored anywhere: the backend forwards a file to the model and keeps nothing, and the
 * archive keeps a file's name only. After a reload the table is empty and the question shows the
 * name, as it always did.
 *
 * The message carries an id, never the `blob:` URL itself. The message is what gets archived, and a
 * blob URL outlives nothing — saved, it would come back from the archive as a broken image.
 *
 * Kept at module level rather than in the thread hook, so closing the window and opening the same
 * conversation again from the archive, without a reload, still finds the picture.
 */
const images = new Map();
/* Each entry holds the whole file in memory until the page goes; past this many, the oldest is let
   go and its question falls back to the name. */
const MAX_IMAGES = 30;
let counter = 0;

/** Keeps a sent file if it is an image the browser can show, and returns the id to find it by. */
export const keepSessionImage = (file) => {
    if (!file || typeof file.type !== "string" || !file.type.startsWith("image/")) return null;
    counter += 1;
    const id = `img-${Date.now()}-${counter}`;
    images.set(id, URL.createObjectURL(file));
    if (images.size > MAX_IMAGES) {
        const [oldest, url] = images.entries().next().value;
        URL.revokeObjectURL(url);
        images.delete(oldest);
    }
    return id;
};

/** The picture for an id from keepSessionImage, or null once it is gone. */
export const sessionImageUrl = (id) => (id && images.get(id)) || null;
